import { GroupPolicyNumberField } from "pages/vehicle/GroupPolicyNumberField";
import { HullProductField } from "pages/vehicle/HullProductField";
import { InsurerBranchOfficeTaxonomyField } from "pages/vehicle/InsurerBranchOfficeTaxonomyField";
import React, { useMemo } from "react";
import { InsurerTaxonomyField } from "../../pages/fleet-form/InsurerTaxonomyField";
import { InsurerVehicleTaxonomyField } from "../../pages/vehicle/InsurerVehicleTaxonomyField";
import { capitalize } from "../../utils/utils";
import { TimelineOperationVehicleCombobox } from "../timeline/controls/TimelineOperationVehicleCombobox";
import { CheckboxField } from "./CheckboxField";
import { ComboboxField } from "./ComboboxField";
import { DateField } from "./DateField";
import DateTimeField from "./DateTimeField";
import { DueDateField } from "./DueDateField";
import { HullTypeComboboxField } from "./HullTypeComboboxField";
import { NumberField } from "./NumberField";
import { RadioGroupFormField } from "./RadioGroupFormField";
import { TaxonomyField } from "./TaxonomyField";
import { TextField } from "./TextField";
import { TextWithOptionsField } from "./TextWithOptionsField";
import { FieldConfig } from "./types";

export const useFields = (
  config: FieldConfig[] | FieldConfig[][],
  applyConfig?: (fc: FieldConfig) => Partial<FieldConfig>,
  setFieldTouched?: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void
) => {
  return useMemo(() => {
    // Flatten config to get all fields for individual field rendering
    const flatConfig = config.flat();

    const fields = flatConfig.reduce((f, c) => {
      if (c.type === "insurerTaxonomy") {
        f[capitalize(c.name)] = (
          <InsurerTaxonomyField key={c.name} fieldConfig={c} />
        );
      }

      if (c.type === "insurerVehicleTaxonomy") {
        f[capitalize(c.name)] = (
          <InsurerVehicleTaxonomyField key={c.name} fieldConfig={c} />
        );
      }

      if (c.type === "insurerBranchOfficeTaxonomy") {
        f[capitalize(c.name)] = (
          <InsurerBranchOfficeTaxonomyField key={c.name} fieldConfig={c} />
        );
      }
      if (c.type === "hullproduct") {
        f[capitalize(c.name)] = (
          <HullProductField key={c.name} fieldConfig={c} />
        );
      }
      if (c.type === "grouppolicynumber") {
        f[capitalize(c.name)] = (
          <GroupPolicyNumberField key={c.name} fieldConfig={c} />
        );
      }
      if (c.type === "number") {
        f[capitalize(c.name)] = <NumberField key={c.name} fieldConfig={c} />;
      }
      if (c.type === "dueDate") {
        f[capitalize(c.name)] = <DueDateField key={c.name} fieldConfig={c} />;
      }
      if (c.type === "text") {
        f[capitalize(c.name)] = <TextField key={c.name} fieldConfig={c} />;
      }
      if (c.type === "textwithoptions") {
        f[capitalize(c.name)] = (
          <TextWithOptionsField key={c.name} fieldConfig={c} />
        );
      }
      if (c.type === "taxonomy") {
        f[capitalize(c.name)] = <TaxonomyField key={c.name} fieldConfig={c} />;
      }
      if (c.type === "combobox") {
        f[capitalize(c.name)] = <ComboboxField key={c.name} fieldConfig={c} />;
      }
      if (c.type === "hulltypecombobox") {
        f[capitalize(c.name)] = (
          <HullTypeComboboxField key={c.name} fieldConfig={c} />
        );
      }
      if (c.type === "timelineoperationvehiclecombobox") {
        f[capitalize(c.name)] = (
          <TimelineOperationVehicleCombobox key={c.name} fieldConfig={c} />
        );
      }
      if (c.type === "checkbox") {
        f[capitalize(c.name)] = <CheckboxField key={c.name} fieldConfig={c} />;
      }
      if (c.type === "radiogroup") {
        f[capitalize(c.name)] = (
          <RadioGroupFormField key={c.name} fieldConfig={c} />
        );
      }
      if (c.type === "date") {
        f[capitalize(c.name)] = <DateField key={c.name} fieldConfig={c} />;
      }
      if (c.type === "datetime") {
        f[capitalize(c.name)] = <DateTimeField key={c.name} fieldConfig={c} />;
      }
      return f;
    }, {});

    // Helper function to render a single field
    const renderField = (c: FieldConfig, index: number) => {
      let fieldConfig = { ...c };
      if (applyConfig) {
        fieldConfig = {
          ...fieldConfig,
          ...applyConfig(fieldConfig),
        } as FieldConfig;
      }

      if (fieldConfig.type === "insurerTaxonomy") {
        return (
          <InsurerTaxonomyField
            key={fieldConfig.name}
            fieldConfig={fieldConfig}
          />
        );
      }
      if (fieldConfig.type === "insurerVehicleTaxonomy") {
        return (
          <InsurerVehicleTaxonomyField
            key={fieldConfig.name}
            fieldConfig={fieldConfig}
          />
        );
      }
      if (fieldConfig.type === "hullproduct") {
        return (
          <HullProductField key={fieldConfig.name} fieldConfig={fieldConfig} />
        );
      }
      if (fieldConfig.type === "grouppolicynumber") {
        return (
          <GroupPolicyNumberField
            key={fieldConfig.name}
            fieldConfig={fieldConfig}
          />
        );
      }
      if (fieldConfig.type === "insurerBranchOfficeTaxonomy") {
        return (
          <InsurerBranchOfficeTaxonomyField
            key={fieldConfig.name}
            fieldConfig={fieldConfig}
          />
        );
      }
      if (fieldConfig.type === "number") {
        return <NumberField key={fieldConfig.name} fieldConfig={fieldConfig} />;
      }
      if (fieldConfig.type === "dueDate") {
        return (
          <DueDateField key={fieldConfig.name} fieldConfig={fieldConfig} />
        );
      }
      if (fieldConfig.type === "text") {
        return <TextField key={fieldConfig.name} fieldConfig={fieldConfig} />;
      }
      if (fieldConfig.type === "textwithoptions") {
        return (
          <TextWithOptionsField
            key={fieldConfig.name}
            fieldConfig={fieldConfig}
          />
        );
      }
      if (
        fieldConfig.name === "paymentMethod" &&
        fieldConfig.type === "taxonomy"
      ) {
        return (
          <TaxonomyField
            key={fieldConfig.name}
            fieldConfig={fieldConfig}
            onSetFieldTouched={() => {
              setFieldTouched && setFieldTouched("motorTaxPaymentMethod", true);
            }}
          />
        );
      }
      if (fieldConfig.type === "taxonomy") {
        return (
          <TaxonomyField key={fieldConfig.name} fieldConfig={fieldConfig} />
        );
      }
      if (fieldConfig.type === "combobox") {
        return (
          <ComboboxField key={fieldConfig.name} fieldConfig={fieldConfig} />
        );
      }
      if (fieldConfig.type === "hulltypecombobox") {
        return (
          <HullTypeComboboxField
            key={fieldConfig.name}
            fieldConfig={fieldConfig}
          />
        );
      }
      if (fieldConfig.type === "timelineoperationvehiclecombobox") {
        return (
          <TimelineOperationVehicleCombobox
            key={fieldConfig.name}
            fieldConfig={fieldConfig}
          />
        );
      }
      if (fieldConfig.type === "checkbox") {
        return (
          <CheckboxField key={fieldConfig.name} fieldConfig={fieldConfig} />
        );
      }
      if (fieldConfig.type === "radiogroup") {
        return (
          <RadioGroupFormField
            key={fieldConfig.name}
            fieldConfig={fieldConfig}
          />
        );
      }
      if (fieldConfig.type === "date") {
        return (
          <DateField
            key={`${fieldConfig.name} ${index}`}
            fieldConfig={fieldConfig}
          />
        );
      }
      if (fieldConfig.type === "datetime") {
        return (
          <DateTimeField
            key={`${fieldConfig.name} ${index}`}
            fieldConfig={fieldConfig}
          />
        );
      }
      return null;
    };

    return {
      ...fields,
      all: (
        <>
          {config.map((item, index) => {
            // If item is an array, render fields in a flex row
            if (Array.isArray(item)) {
              return (
                <div
                  key={`row-${index}`}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "15px",
                    alignItems: "flex-start",
                    width: "100%",
                  }}
                >
                  {item.map((fieldConfig, fieldIndex) => (
                    <div
                      key={`${fieldConfig.name}-${fieldIndex}`}
                      style={{ flex: 1 }}
                    >
                      {renderField(fieldConfig, fieldIndex)}
                    </div>
                  ))}
                </div>
              );
            } else {
              // Single field, render normally
              return renderField(item, index);
            }
          })}
        </>
      ),
    };
  }, [applyConfig, config, setFieldTouched]);
};
